import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant, statusLabel } from "../components/Badge";
import { Check, X, Plus, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtBDT } from "../lib/format";
import NumberInput from "../../components/ui/NumberInput";

export default function Deposits({ onlyPending = false }) {
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterMethod, setFilterMethod] = useState("All");
  const [action, setAction] = useState(null); // { row, status }
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ player_user_id: "", amount: "", payment_method: "bKash", note: "" });

  const load = async () => {
    setLoading(true);
    const params = onlyPending ? { status: "pending" } : {};
    const { data } = await api.get("/deposits", { params });
    setRows(data);
    setSelected(new Set());
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyPending]);

  const methods = useMemo(() => ["All", ...Array.from(new Set(rows.map(r => r.payment_method).filter(Boolean)))], [rows]);
  const filtered = filterMethod === "All" ? rows : rows.filter(r => r.payment_method === filterMethod);

  const confirmAction = async () => {
    try {
      await api.patch(`/deposits/${action.row.id}`, { status: action.status, note });
      toast.success(`Deposit ${action.status}`);
      setAction(null); setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const bulkAction = async (status) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${status === "approved" ? "Approve" : "Reject"} ${ids.length} deposit(s)?`)) return;
    const results = await Promise.allSettled(ids.map(id => api.patch(`/deposits/${id}`, { status })));
    const ok = results.filter(r => r.status === "fulfilled").length;
    toast.success(`${ok}/${ids.length} deposits ${status}`);
    load();
  };

  const createManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/deposits", { ...form, amount: Number(form.amount), auto_approve: true });
      toast.success("Manual deposit credited");
      setShowCreate(false);
      setForm({ player_user_id: "", amount: "", payment_method: "bKash", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const toggleAll = () => {
    const pending = filtered.filter(r => r.status === "pending");
    if (selected.size === pending.length && pending.length > 0) setSelected(new Set());
    else setSelected(new Set(pending.map(r => r.id)));
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (onlyPending) params.set("status", "pending");
      const { data } = await api.get(`/deposits/download.pdf?${params.toString()}`, { responseType: "blob" });
      const ts = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-${onlyPending ? "pending" : "all"}-deposits-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="deposits-page">
      <PageHeader
        title={onlyPending ? "Pending Deposits" : "All Deposits"}
        subtitle={onlyPending ? "Review, filter, approve or reject deposit requests" : "Full deposit ledger with filters and manual entry"}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="h-9 px-3 inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-emerald-300 disabled:opacity-50 text-gray-700 text-sm rounded-sm"
              data-testid="deposit-download-pdf"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Download PDF
            </button>
            <button onClick={() => setShowCreate(true)} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5" data-testid="deposit-manual-btn">
              <Plus size={14} /> Manual deposit
            </button>
          </div>
        }
      />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Method</span>
          {methods.map(m => (
            <button key={m} onClick={() => setFilterMethod(m)} className={`h-8 px-3 text-xs rounded-sm border ${filterMethod === m ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200 text-gray-700 hover:border-emerald-300"}`} data-testid={`deposit-filter-${m}`}>
              {m}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-actions">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button onClick={() => bulkAction("approved")} className="h-8 px-3 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm inline-flex items-center gap-1" data-testid="bulk-approve"><Check size={12} /> Approve</button>
            <button onClick={() => bulkAction("rejected")} className="h-8 px-3 text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm inline-flex items-center gap-1" data-testid="bulk-reject"><X size={12} /> Reject</button>
          </div>
        )}
      </div>

      <DataTable
        testId="deposits-table"
        loading={loading}
        rows={filtered}
        columns={[
          {
            key: "sel", mobileHide: true, label: (
              <input type="checkbox" onChange={toggleAll} checked={filtered.filter(r => r.status === "pending").length > 0 && selected.size === filtered.filter(r => r.status === "pending").length} data-testid="bulk-select-all" />
            ), render: (r) => r.status === "pending" ? (
              <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} onClick={(e) => e.stopPropagation()} data-testid={`select-${r.id}`} />
            ) : null
          },
          { key: "txn_id", label: "Txn ID" },
          {
            key: "player",
            label: "Player",
            searchKeys: ["player_user_id", "player_id", "player_name"],
            render: (r) => (
              <div className="leading-tight">
                <div className="text-sm text-gray-900 font-medium truncate max-w-[180px]" title={r.player_user_id || r.player_name || ""}>
                  {r.player_user_id || r.player_name || "—"}
                </div>
                {r.player_id ? (
                  <div className="text-[11px] text-gray-500 font-mono truncate max-w-[180px]" title={`Player ID: ${r.player_id}`}>
                    ID: {r.player_id}
                  </div>
                ) : null}
              </div>
            ),
          },
          { key: "amount", label: "Amount", align: "right", render: (r) => fmtBDT(r.amount) },
          { key: "payment_method", label: "Method" },
          {
            key: "gateway",
            label: "Gateway / Order #",
            searchKeys: ["gateway_label", "gateway_order_no"],
            render: (r) => {
              const label = r.gateway_label || "";
              const orderNo = r.gateway_order_no || "";
              if (!label && !orderNo) return <span className="text-gray-400 text-xs">—</span>;
              return (
                <div className="leading-tight" data-testid={`dep-gateway-${r.id}`}>
                  {label ? (
                    <div className="text-sm text-gray-900 font-medium truncate max-w-[160px]" title={label}>
                      {label}
                    </div>
                  ) : null}
                  {orderNo ? (
                    <div
                      className="text-[11px] text-gray-500 font-mono truncate max-w-[160px] cursor-pointer select-all"
                      title={`Gateway order #: ${orderNo} (click to copy)`}
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          navigator.clipboard?.writeText(orderNo);
                          toast.success("Order # copied");
                        } catch {
                          /* ignore */
                        }
                      }}
                    >
                      #{orderNo}
                    </div>
                  ) : null}
                </div>
              );
            },
          },
          { key: "status", label: "Status", render: (r) => <Badge variant={statusVariant(r.status)}>{statusLabel(r.status)}</Badge> },
          { key: "admin_note", label: "Note", render: (r) => <span className="text-xs text-gray-500 truncate max-w-[120px] inline-block">{r.admin_note || "—"}</span> },
          { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleString("en-GB") },
        ]}
        actions={(row) => row.status === "pending" ? (
          <div className="flex flex-wrap items-center gap-1 justify-end">
            <button onClick={() => { setAction({ row, status: "approved" }); setNote(""); }} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50" data-testid={`deposit-approve-${row.id}`}>
              <Check size={13} /> Approve
            </button>
            <button onClick={() => { setAction({ row, status: "rejected" }); setNote(""); }} className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-red-200 text-red-700 rounded-sm hover:bg-red-50" data-testid={`deposit-reject-${row.id}`}>
              <X size={13} /> Reject
            </button>
          </div>
        ) : null}
      />

      {action && (
        <Modal title={`${action.status === "approved" ? "Approve" : "Reject"} deposit`} onClose={() => setAction(null)}>
          <div className="text-sm text-gray-600 mb-3">
            <div>Player: <b>{action.row.player_name}</b></div>
            <div>Amount: <b>{fmtBDT(action.row.amount)}</b></div>
            <div>Method: {action.row.payment_method}</div>
          </div>
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Admin note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" data-testid="action-note" />
          <div className="flex gap-2 mt-4">
            <button onClick={() => setAction(null)} className="flex-1 h-10 border border-gray-200 rounded-sm text-sm">Cancel</button>
            <button onClick={confirmAction} className={`flex-1 h-10 text-white font-semibold rounded-sm text-sm ${action.status === "approved" ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"}`} data-testid="action-confirm">
              {action.status === "approved" ? "Approve" : "Reject"}
            </button>
          </div>
        </Modal>
      )}

      {showCreate && (
        <Modal title="Manual deposit entry" onClose={() => setShowCreate(false)}>
          <form onSubmit={createManual} className="space-y-3">
            <Field label="Player Username" value={form.player_user_id} onChange={(v) => setForm({ ...form, player_user_id: v })} testId="create-username" required />
            <Field label="Amount (BDT)" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="create-amount" required />
            <Field label="Payment Method" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} testId="create-method" />
            <Field label="Note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} testId="create-note" />
            <div className="text-xs text-gray-500">Will auto-approve and credit the player's balance.</div>
            <button type="submit" className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm" data-testid="create-submit">
              Credit player
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  const isNumber = type === "number";
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      {isNumber ? (
        <NumberInput value={value} onChange={(raw) => onChange(raw)} asString required={required} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-display font-bold text-base">{title}</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center text-gray-500 hover:text-gray-900"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
